import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useCallback, useEffect, useRef, useState } from 'react';
import useResponsive from 'src/hooks/useResponsive';
import { ChatMessage } from 'src/interfaces/Chat';
import BotMessage from './BotMessage/BotMessage';
import { EmptyChatComponent } from './Chat/EmptyChatComponent';
import UserMessage from './UserMessage';

interface ChatArea {
  messages: ChatMessage[];
  streaming?: boolean;
  username?: string;
  sendMessage: (
    message: string,
    read_url: string | undefined,
    subject?: string
  ) => void;
  subjects?: string[];
}

export const ChatArea = (props: ChatArea) => {
  const { messages, streaming, username, sendMessage } = props;

  const viewport: any = useRef<HTMLDivElement>(null);
  const [isAtBottomButtonTrigger, setAtBottomButtonTrigger] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showOptions, setShowOptions] = useState(false);
  const { isMobile } = useResponsive();

  useEffect(() => {
    scrollToBottom();
  }, [showOptions]);

  useEffect(() => {
    // Scroll to bottom on initial page load
    scrollToBottom();

    const handleScrollButtonTrigger = () => {
      if (!viewport.current) {
        return;
      }

      // Calculate the difference and check if it's within a threshold
      const bottomThreshold = isMobile ? 120 : 100; // pixels from the bottom

      const isBottom =
        viewport.current.scrollHeight - viewport.current.scrollTop <=
        viewport.current.clientHeight + bottomThreshold;
      setAtBottomButtonTrigger(isBottom);
    };

    const handleScroll = () => {
      if (!viewport.current) {
        return;
      }

      // Calculate the difference and check if it's within a threshold
      const bottomThreshold = isMobile ? 30 : 50; // pixels from the bottom
      const isBottom =
        viewport.current.scrollHeight - viewport.current.scrollTop <=
        viewport.current.clientHeight + bottomThreshold;
      setIsAtBottom(isBottom);
    };

    viewport.current?.addEventListener('scroll', handleScrollButtonTrigger);
    viewport.current?.addEventListener('scroll', handleScroll);

    return () => {
      viewport.current?.removeEventListener(
        'scroll',
        handleScrollButtonTrigger
      );
      viewport.current?.removeEventListener('scroll', handleScroll);
    };
  }, [messages]);

  useEffect(() => {
    if (streaming) {
      if (isAtBottom) {
        scrollToBottom();
      }
    }
  }, [messages, streaming]);

  const handleOpenOptions = useCallback(() => {
    setShowOptions((prev) => !prev);
  }, []);

  const closeOptions = useCallback(() => {
    setShowOptions(false);
  }, []);

  const scrollToBottom = () => {
    viewport.current.scrollTo({
      top: viewport.current.scrollHeight,
      behavior: 'smooth',
    });
  };

  return (
    <div className="relative h-full w-full flex flex-col">
      <div
        ref={viewport}
        className="h-full overflow-y-auto no-scrollbar py-8 px-2 space-y-8 flex-1"
      >
        {messages.length > 0 ? (
          messages?.map((item, index) => {
            let isLastMessage = index === messages.length - 1;
            if (item.role == 'human') {
              return <UserMessage message={item.content} key={index} />;
            } else {
              return isLastMessage ? (
                <BotMessage
                  key={index}
                  index={index}
                  showOptions={showOptions}
                  message={item.content}
                  isLatest={isLastMessage}
                  toggleOptions={handleOpenOptions}
                  closeOptions={closeOptions}
                  streaming={streaming}
                  image_url={item.image_url}
                />
              ) : (
                <BotMessage
                  key={index}
                  index={index}
                  message={item.content}
                  isLatest={isLastMessage}
                  streaming={streaming}
                  image_url={item.image_url}
                />
              );
            }
          })
        ) : (
          <EmptyChatComponent
            sendMessage={sendMessage}
            username={username ? username : ''}
          />
        )}
      </div>
      {!isAtBottomButtonTrigger && (
        <div className="absolute inset-x-0 bottom-20 flex justify-center">
          <ArrowDownwardIcon
            onClick={scrollToBottom}
            className="cursor-pointer z-40 p-1 rounded-full text-lg text-theme-color-4 bg-theme-color-2"
            style={{ fontSize: '2rem' }}
          />
        </div>
      )}
    </div>
  );
};
